import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, push, set } from 'firebase/database';
import { database } from '../config/firebase';
import './Configurator.css';

interface Option {
  id: string;
  name: string;
  desc: string;
  pricePerM2: number; // unit price per square meter
  image?: string;
}

interface ChairOption {
  id: string;
  name: string;
  desc: string;
  pricePerSeat: number;
  image: string;
}

interface SizeOption {
  id: string;
  name: string;
  desc: string;
  area: number; // m²
  seats: number;
}

// ---- Hall sizes ----
const SIZES: SizeOption[] = [
  { id: 'small', name: 'Boutique Hall', desc: 'Up to ~100 seats', area: 150, seats: 100 },
  { id: 'medium', name: 'Standard Hall', desc: '~250 seats', area: 350, seats: 250 },
  { id: 'large', name: 'Grand Hall', desc: '~450 seats', area: 600, seats: 450 },
  { id: 'xlarge', name: 'Mega Auditorium', desc: '~750 seats', area: 1000, seats: 750 },
];

// ---- Ceiling materials ----
const CEILINGS: Option[] = [
  { id: 'gypsum', name: 'Acoustic Gypsum', desc: 'Cost-effective smooth finish', pricePerM2: 28, image: '/yapiakustikcozumler/akustiktavan.jpg.avif' },
  { id: 'panel', name: 'Acoustic Panel Ceiling', desc: 'High sound absorption', pricePerM2: 48, image: '/yapiakustikcozumler/akustiktavan.jpg.avif' },
  { id: 'wood', name: 'Wooden Acoustic Ceiling', desc: 'Premium warm aesthetic', pricePerM2: 82, image: '/yapiakustikcozumler/akustiktavan.jpg.avif' },
  { id: 'stretch', name: 'Stretch (Barrisol) Ceiling', desc: 'Seamless modern look', pricePerM2: 65, image: '/yapiakustikcozumler/akustiktavan.jpg.avif' },
];

// ---- Wall materials ----
const WALLS: Option[] = [
  { id: 'plaster', name: 'Acoustic Plaster', desc: 'Smooth seamless absorber', pricePerM2: 42, image: '/yapiakustikcozumler/akustikduvar.jpg.avif' },
  { id: 'fabric', name: 'Acoustic Fabric Panel', desc: 'Wide color & texture range', pricePerM2: 58, image: '/yapiakustikcozumler/akustikduvar.jpg.avif' },
  { id: 'micro', name: 'Microperforated Panel', desc: 'Wood/aluminium perforated', pricePerM2: 74, image: '/yapiakustikcozumler/akustikduvar.jpg.avif' },
  { id: 'woodwall', name: 'Wooden Acoustic Wall', desc: 'Premium diffuser walls', pricePerM2: 92, image: '/yapiakustikcozumler/akustikduvar.jpg.avif' },
];

// ---- Chairs (pulled from our catalogue) ----
const CHAIRS: ChairOption[] = [
  { id: 'max', name: 'Max', desc: 'Economic conference chair', pricePerSeat: 195, image: '/max.jpg.avif' },
  { id: 'rona', name: 'Rona', desc: 'Comfortable everyday seating', pricePerSeat: 235, image: '/rona.jpg.avif' },
  { id: 'mod', name: 'Mod', desc: 'Modern ergonomic design', pricePerSeat: 275, image: '/mod.jpg.avif' },
  { id: 'belen', name: 'Belen', desc: 'Elegant upholstered chair', pricePerSeat: 310, image: '/belen.jpg.avif' },
  { id: 'loft', name: 'Loft', desc: 'Premium flagship model', pricePerSeat: 385, image: 'https://static.wixstatic.com/media/e733f0_4c1a0cb8d8674ce394564a0de76241ab~mv2.jpg/v1/fill/w_800,h_800,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/e733f0_4c1a0cb8d8674ce394564a0de76241ab~mv2.jpg' },
  { id: 'plus', name: 'Plus Cinema', desc: 'Luxury recliner cinema seat', pricePerSeat: 460, image: '/plussinema.jpg.avif' },
];

// ---- Sound systems (price per m²) ----
const SOUND: Option[] = [
  { id: 'basic', name: 'Basic PA System', desc: 'Speech-focused coverage', pricePerM2: 9 },
  { id: 'line', name: 'Line Array System', desc: 'Even full-hall coverage', pricePerM2: 19 },
  { id: 'immersive', name: 'Immersive (Dolby Atmos)', desc: 'Cinematic 3D audio', pricePerM2: 36 },
];

// ---- Lighting / LED (price per m²) ----
const LIGHTING: Option[] = [
  { id: 'led', name: 'Standard LED Lighting', desc: 'Energy-efficient general light', pricePerM2: 12 },
  { id: 'rgb', name: 'RGB Stage Lighting', desc: 'Dynamic colored scenes', pricePerM2: 24 },
  { id: 'dmx', name: 'Premium DMX System', desc: 'Full programmable show lighting', pricePerM2: 41 },
];

// ---- Floor (FIXED / included) ----
const FLOOR = { name: 'Premium Acoustic Carpet Flooring', pricePerM2: 30 };

const WALL_FACTOR = 0.8; // wall area relative to floor area

type Selections = {
  size: SizeOption | null;
  ceiling: Option | null;
  wall: Option | null;
  chair: ChairOption | null;
  sound: Option | null;
  lighting: Option | null;
};

const STEPS = ['size', 'ceiling', 'wall', 'chair', 'sound', 'lighting', 'summary'] as const;

const STEP_TITLES: Record<string, { title: string; subtitle: string }> = {
  size: { title: 'Hall Size', subtitle: 'How large is your venue?' },
  ceiling: { title: 'Ceiling Material', subtitle: 'Choose your acoustic ceiling' },
  wall: { title: 'Wall Material', subtitle: 'Choose your acoustic walls' },
  chair: { title: 'Seating', subtitle: 'Select chairs from our catalogue' },
  sound: { title: 'Sound System', subtitle: 'Pick your audio setup' },
  lighting: { title: 'Lighting & LED', subtitle: 'Pick your lighting setup' },
  summary: { title: 'Your Estimate', subtitle: 'Review your custom hall' },
};

const fmt = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

const Configurator: React.FC = () => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [sel, setSel] = useState<Selections>({
    size: null, ceiling: null, wall: null, chair: null, sound: null, lighting: null,
  });

  // contact form
  const [name, setName] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const step = STEPS[stepIndex];

  const area = sel.size?.area ?? 0;
  const seats = sel.size?.seats ?? 0;

  const breakdown = useMemo(() => {
    const ceiling = sel.ceiling ? sel.ceiling.pricePerM2 * area : 0;
    const wall = sel.wall ? sel.wall.pricePerM2 * area * WALL_FACTOR : 0;
    const chairs = sel.chair ? sel.chair.pricePerSeat * seats : 0;
    const sound = sel.sound ? sel.sound.pricePerM2 * area : 0;
    const lighting = sel.lighting ? sel.lighting.pricePerM2 * area : 0;
    const floor = area ? FLOOR.pricePerM2 * area : 0;
    const total = ceiling + wall + chairs + sound + lighting + floor;
    return { ceiling, wall, chairs, sound, lighting, floor, total };
  }, [sel, area, seats]);

  const canProceed = (() => {
    switch (step) {
      case 'size': return !!sel.size;
      case 'ceiling': return !!sel.ceiling;
      case 'wall': return !!sel.wall;
      case 'chair': return !!sel.chair;
      case 'sound': return !!sel.sound;
      case 'lighting': return !!sel.lighting;
      default: return true;
    }
  })();

  const next = () => { if (stepIndex < STEPS.length - 1) { setDir(1); setStepIndex(stepIndex + 1); } };
  const prev = () => { if (stepIndex > 0) { setDir(-1); setStepIndex(stepIndex - 1); } };

  const choose = <K extends keyof Selections>(key: K, value: Selections[K]) => {
    setSel((s) => ({ ...s, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!name.trim() || !emailVal.trim() || !phone.trim()) {
      setFormError('Please fill in your name, email and phone.');
      return;
    }
    setSubmitting(true);
    try {
      const reqRef = ref(database, 'configuratorRequests');
      const newRef = push(reqRef);
      await set(newRef, {
        contact: { name: name.trim(), email: emailVal.trim(), phone: phone.trim() },
        selections: {
          size: sel.size ? `${sel.size.name} (${sel.size.area} m², ~${sel.size.seats} seats)` : '',
          ceiling: sel.ceiling?.name ?? '',
          wall: sel.wall?.name ?? '',
          chair: sel.chair?.name ?? '',
          sound: sel.sound?.name ?? '',
          lighting: sel.lighting?.name ?? '',
          floor: FLOOR.name,
        },
        estimatedBudget: Math.round(breakdown.total),
        currency: 'USD',
        createdAt: new Date().toISOString(),
        status: 'new',
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Configurator submit error:', err);
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderCards = (
    options: Option[],
    selected: Option | null,
    key: keyof Selections,
    unit: 'm2' | 'seat',
  ) => (
    <div className="cfg-grid">
      {options.map((o) => {
        const isSel = selected?.id === o.id;
        const price = unit === 'm2' ? o.pricePerM2 * area : 0;
        return (
          <button
            key={o.id}
            className={`cfg-card ${isSel ? 'selected' : ''}`}
            onClick={() => choose(key, o as any)}
            type="button"
          >
            {o.image && <div className="cfg-card-img" style={{ backgroundImage: `url(${o.image})` }} />}
            <div className="cfg-card-body">
              <div className="cfg-card-top">
                <h3>{o.name}</h3>
                {isSel && <span className="cfg-check">✓</span>}
              </div>
              <p>{o.desc}</p>
              <div className="cfg-card-price">
                {area ? <>{fmt(price)} <span>· {fmt(o.pricePerM2)}/m²</span></> : <>{fmt(o.pricePerM2)}/m²</>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="cfg-page">
      {/* Header / progress */}
      <div className="cfg-header">
        <button className="cfg-back-home" onClick={() => navigate('/')}>← Home</button>
        <div className="cfg-progress">
          {STEPS.slice(0, -1).map((s, i) => (
            <div key={s} className={`cfg-progress-dot ${i <= stepIndex ? 'done' : ''}`} />
          ))}
        </div>
        <div className="cfg-running">
          <span>Estimated</span>
          <strong>{fmt(breakdown.total)}</strong>
        </div>
      </div>

      <div className="cfg-step-head">
        <span className="cfg-step-count">Step {Math.min(stepIndex + 1, STEPS.length)} / {STEPS.length}</span>
        <h1>{STEP_TITLES[step].title}</h1>
        <p>{STEP_TITLES[step].subtitle}</p>
      </div>

      <div className="cfg-stage">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            initial={{ opacity: 0, x: dir * 80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -80 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="cfg-panel"
          >
            {step === 'size' && (
              <div className="cfg-grid">
                {SIZES.map((o) => {
                  const isSel = sel.size?.id === o.id;
                  return (
                    <button key={o.id} type="button" className={`cfg-card size ${isSel ? 'selected' : ''}`} onClick={() => choose('size', o)}>
                      <div className="cfg-card-body">
                        <div className="cfg-card-top">
                          <h3>{o.name}</h3>
                          {isSel && <span className="cfg-check">✓</span>}
                        </div>
                        <p>{o.desc}</p>
                        <div className="cfg-size-meta">
                          <span><strong>{o.area}</strong> m²</span>
                          <span><strong>{o.seats}</strong> seats</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 'ceiling' && renderCards(CEILINGS, sel.ceiling, 'ceiling', 'm2')}
            {step === 'wall' && renderCards(WALLS, sel.wall, 'wall', 'm2')}

            {step === 'chair' && (
              <div className="cfg-grid chairs">
                {CHAIRS.map((o) => {
                  const isSel = sel.chair?.id === o.id;
                  return (
                    <button key={o.id} type="button" className={`cfg-card chair ${isSel ? 'selected' : ''}`} onClick={() => choose('chair', o)}>
                      <div className="cfg-card-img chair" style={{ backgroundImage: `url(${o.image})` }} />
                      <div className="cfg-card-body">
                        <div className="cfg-card-top">
                          <h3>{o.name}</h3>
                          {isSel && <span className="cfg-check">✓</span>}
                        </div>
                        <p>{o.desc}</p>
                        <div className="cfg-card-price">
                          {seats ? <>{fmt(o.pricePerSeat * seats)} <span>· {fmt(o.pricePerSeat)}/seat</span></> : <>{fmt(o.pricePerSeat)}/seat</>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 'sound' && renderCards(SOUND, sel.sound, 'sound', 'm2')}
            {step === 'lighting' && renderCards(LIGHTING, sel.lighting, 'lighting', 'm2')}

            {step === 'summary' && (
              <div className="cfg-summary">
                {!submitted ? (
                  <>
                    <div className="cfg-summary-grid">
                      <div className="cfg-summary-list">
                        <Row label="Hall Size" value={sel.size ? `${sel.size.name} · ${sel.size.area} m² · ~${sel.size.seats} seats` : '-'} price={breakdown.floor + breakdown.ceiling + breakdown.wall + breakdown.chairs + breakdown.sound + breakdown.lighting === 0 ? 0 : undefined} hidePrice />
                        <Row label="Ceiling" value={sel.ceiling?.name} price={breakdown.ceiling} />
                        <Row label="Walls" value={sel.wall?.name} price={breakdown.wall} />
                        <Row label={`Seating × ${seats}`} value={sel.chair?.name} price={breakdown.chairs} />
                        <Row label="Sound System" value={sel.sound?.name} price={breakdown.sound} />
                        <Row label="Lighting" value={sel.lighting?.name} price={breakdown.lighting} />
                        <Row label="Flooring (included)" value={FLOOR.name} price={breakdown.floor} />
                        <div className="cfg-total-row">
                          <span>Estimated Budget</span>
                          <strong>{fmt(breakdown.total)}</strong>
                        </div>
                        <p className="cfg-disclaimer">* This is an indicative estimate. Final pricing is confirmed after a technical site survey by our engineers.</p>
                      </div>

                      <form className="cfg-contact" onSubmit={handleSubmit}>
                        <h3>Get a Detailed Quote</h3>
                        <p>Share your details and our team will reach out with a tailored proposal.</p>
                        <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
                        <input type="email" placeholder="Email Address" value={emailVal} onChange={(e) => setEmailVal(e.target.value)} />
                        <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        {formError && <div className="cfg-form-error">{formError}</div>}
                        <button type="submit" className="cfg-submit" disabled={submitting}>
                          {submitting ? 'Sending...' : 'Get More Information →'}
                        </button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="cfg-success">
                    <div className="cfg-success-icon">✓</div>
                    <h2>Thank you, {name.split(' ')[0]}!</h2>
                    <p>Your custom hall request has been sent to our team. We will contact you shortly with a detailed proposal.</p>
                    <div className="cfg-success-budget">Estimated Budget: <strong>{fmt(breakdown.total)}</strong></div>
                    <button className="cfg-submit" onClick={() => navigate('/')}>Back to Home</button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer nav */}
      {!submitted && (
        <div className="cfg-nav">
          <button className="cfg-btn ghost" onClick={prev} disabled={stepIndex === 0}>Back</button>
          {step !== 'summary' ? (
            <button className="cfg-btn primary" onClick={next} disabled={!canProceed}>
              Continue →
            </button>
          ) : (
            <div className="cfg-nav-total">Total: <strong>{fmt(breakdown.total)}</strong></div>
          )}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string; price?: number; hidePrice?: boolean }> = ({ label, value, price, hidePrice }) => (
  <div className="cfg-row">
    <div className="cfg-row-left">
      <span className="cfg-row-label">{label}</span>
      <span className="cfg-row-value">{value || '-'}</span>
    </div>
    {!hidePrice && <span className="cfg-row-price">{price ? fmt(price) : '-'}</span>}
  </div>
);

export default Configurator;
